import AceEditor from 'react-ace';
import { addCompleter } from 'ace-builds/src-noconflict/ext-language_tools';
import { useEffect, useRef } from 'react';
import { Button, Stack } from '@mui/material';
import { helpers } from './helperfunctions';
import ReactAce from 'react-ace/lib/ace';
import { useResource } from 'rest-hooks';
import { VariableResource } from '../../model/resources/variableResource';
import { XmlQuerySelector } from './xmlQuerySelector';
import { XmlExpressionBuilder } from './xmlExpressionBuilder';
import { FormattedMessage } from 'react-intl';

export type XmlEditorVariant = 'variable' | 'expression';

interface XmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name?: string;
  variant?: XmlEditorVariant;
}

interface AceCompletion {
  name: string;
  value: string;
  meta: string;
}

/**
 * Codeblock editor for xml/xslt code using react-ace
 */
export const XmlEditor = (props: XmlEditorProps) => {
  const { value, onChange, onBlur, name, variant } = props;

  const aceRef = useRef<ReactAce>(null);

  const variables = useResource(VariableResource.list(), {});

  useEffect(() => {
    const completer = {
      getCompletions: function (
        _1: never,
        _2: never,
        _3: never,
        _4: never,
        callback: (_: null, competions: AceCompletion[]) => void
      ) {
        const completions = variables.map<AceCompletion>((variable) => ({
          name: `$${variable.name}`,
          value: `$${variable.name}`,
          meta: 'variable'
        }));
        callback(null, completions);
      }
    };

    addCompleter(completer);
  }, [variables]);

  //Inserts text at cursorposistion or at end of value
  const insertText = (text: string) => {
    aceRef.current?.editor.insert(text);
  };

  return (
    <>
      <AceEditor
        ref={aceRef}
        mode="xml"
        theme="tomorrow"
        name={name}
        width="100%"
        minLines={4}
        maxLines={Infinity}
        fontSize={'1rem'}
        value={value}
        onChange={(value) => {
          onChange(value);
        }}
        onBlur={() => onBlur()}
        enableSnippets
        enableBasicAutocompletion
        enableLiveAutocompletion
      />
      {variant === 'variable' && (
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <XmlQuerySelector handleQuery={(path: string) => insertText(helpers['value of'](path))} />
          {Object.entries(helpers).map(([key, helperFunction]) => (
            <Button
              variant="outlined"
              size="small"
              key={key}
              onClick={() => insertText(helperFunction(''))}>
              {key}
            </Button>
          ))}
        </Stack>
      )}
      {variant === 'expression' && (
        <Stack direction="row" spacing={1}>
          <XmlExpressionBuilder handleExpression={(expression: string) => insertText(expression)} />
          <Button size="small" variant="outlined" onClick={() => insertText(' and ')}>
            <FormattedMessage id="expression.editor.helpers.and" defaultMessage="And" />
          </Button>
          <Button size="small" variant="outlined" onClick={() => insertText(' or ')}>
            <FormattedMessage id="expression.editor.helpers.or" defaultMessage="Or" />
          </Button>
        </Stack>
      )}
    </>
  );
};
